import React, { useEffect } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  CardFooter,
  Button,
  Text,
} from '@patternfly/react-core';
import ArrowRightIcon from '@patternfly/react-icons/dist/esm/icons/arrow-right-icon';
import { useFeature } from '@ole-ui/ole-ui-components';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import useMobxStores from 'hooks/useMobxStores';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import AccessLearningSubscription from 'components/NoEnrollment/AccessLearningSubscription';
import FreeSkillsAssessment from 'components/NoEnrollment/FreeSkillsAssessment';
import { EnablementFeatureFlags } from 'config/featureFlags';
import ExploreSubscriptions from 'components/ExploreSubscriptions';
import {
  REDHAT_RHLS_HOW_TO_BUY_URL,
  SKILLS_ASSESSMENT_URL,
} from 'config/constants';
import Banners from './Banners';

import './styles.scss';
import LastAccessedOffering from './LastAccessedOffering';
import UpcomingLiveEvents from './UpcomingLiveEvents';

// When we switch to router with nested routes, we should remove this props.
const MyActivity = ({ onMyTrainingClick }) => {
  const { t } = useTranslation();
  const { routerStore, classesStore, userStore } = useMobxStores();
  const { hasCurrentEnrollment } = classesStore;
  const { hasActiveSubscription, isRhlsCourseOnlyUser: isUserOnlyRhlsCourse } =
    userStore;
  const isNewMyTrainingEnabled = useFeature({
    defaultFlagValue: false,
    flagName: EnablementFeatureFlags.NewMyTraining,
    section: 'enablements',
  });

  const showDiscoverAndPlanSection =
    useFeature({
      section: 'enablements',
      flagName: EnablementFeatureFlags.ShowDiscoverAndPlanSection,
      defaultFlagValue: false,
    }) && hasCurrentEnrollment;

  useEffect(() => {
    classesStore.getClassesForCurrentUser();
    classesStore.getPremiumSessionEnrollmentsForCurrentUser();
  }, []);

  return (
    <div className="my-activity">
      <div className="my-activity__content">
        <Banners />

        {hasCurrentEnrollment ? (
          <div className="my-activity__section">
            <div className="my-activity__lane">
              <LastAccessedOffering />
              <UpcomingLiveEvents />
            </div>
          </div>
        ) : (
          <div className="my-activity__no-enrollment-section">
            <AccessLearningSubscription />
            <FreeSkillsAssessment />
          </div>
        )}

        {showDiscoverAndPlanSection ? (
          <section className="my-activity__section">
            <h3>{t('Discover and plan')}</h3>
            <div className="my-activity__grid">
              {userStore.isRHLSTrialSubscriber && isNewMyTrainingEnabled && (
                <ExploreSubscriptions
                  isRHLSTrialCopy
                  className="explore-subscriptions--homepage"
                />
              )}
              <Card
                ouiaId="MyTrainingCard"
                isFlat
                isRounded
                className="my-activity__card"
              >
                <CardTitle>{t('My training')}</CardTitle>
                <CardBody>
                  {t(
                    'Access your training, discover new opportunities, and get personalized recommendations.',
                  )}
                </CardBody>
                <CardFooter>
                  <Button
                    className="my-activity__card-btn"
                    variant="link"
                    icon={<ArrowRightIcon />}
                    iconPosition="end"
                    onClick={onMyTrainingClick}
                    data-analytics-id="my-training-btn-my-activity-view-lp"
                    data-testid="view-my-training-btn"
                  >
                    {t('View')}
                  </Button>
                </CardFooter>
              </Card>
              <Card isFlat isRounded className="my-activity__card">
                <CardTitle>{t('Catalog')}</CardTitle>
                <CardBody>
                  {t(
                    'Discover a curated selection of courses, resources, and offerings designed to enhance your knowledge and expertise.',
                  )}
                </CardBody>
                <CardFooter>
                  <Button
                    className="my-activity__card-btn"
                    variant="link"
                    icon={<ArrowRightIcon />}
                    iconPosition="end"
                    data-analytics-id="catalog-btn-my-activity-view-lp"
                    onClick={() => routerStore.navigate('catalog')}
                  >
                    {t('Explore')}
                  </Button>
                </CardFooter>
              </Card>

              {/* TODO: in the near future we want a feature flag to know if user has access to SP or not. */}
              {hasActiveSubscription ? (
                <Card isFlat isRounded className="my-activity__card">
                  <CardTitle>{t('Skills Paths')}</CardTitle>
                  <CardBody>
                    {t(
                      'Enroll in a pre-defined Skills Path or craft your unique learning journey to gain skills precisely aligned to your career goals.',
                    )}
                  </CardBody>
                  <CardFooter>
                    <Button
                      className="my-activity__card-btn"
                      variant="link"
                      icon={<ArrowRightIcon />}
                      iconPosition="end"
                      data-analytics-id="sp-btn-my-activity-view-lp"
                      onClick={() => routerStore.navigate('skills-paths')}
                    >
                      {t('Discover')}
                    </Button>
                  </CardFooter>
                </Card>
              ) : null}

              <Card isFlat isRounded className="my-activity__card">
                <CardTitle>{t('Skills Assessment')}</CardTitle>
                <CardBody>
                  {t(
                    'Test your expertise, determine your skills gaps, and receive personalized recommendations with Red Hat training.',
                  )}
                </CardBody>
                <CardFooter>
                  <Button
                    component="a"
                    className="my-activity__card-btn"
                    variant="link"
                    icon={<ArrowRightIcon />}
                    iconPosition="end"
                    data-analytics-id="sa-btn-my-activity-view-lp"
                    href={SKILLS_ASSESSMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('Access')}
                  </Button>
                </CardFooter>
              </Card>

              {isUserOnlyRhlsCourse ? (
                <Card
                  isFlat
                  isRounded
                  className="my-activity__card my-activity__card--dark"
                  // TODO: in the future use Patternfly dark mode
                >
                  <CardTitle>{t('Ready to unlock more?')}</CardTitle>
                  <CardBody>
                    <Text component="p">
                      {t(
                        'Upgrade your subscription to enjoy full offerings and exclusive features. See the difference and choose the plan that suits you best.',
                      )}
                    </Text>
                  </CardBody>
                  <CardFooter>
                    <Button
                      component="a"
                      className="my-activity__card-btn pf-v5-theme-dark"
                      variant="link"
                      icon={<ArrowRightIcon />}
                      iconPosition="end"
                      data-analytics-id="how-to-buy-btn-my-activity-view-lp"
                      href={REDHAT_RHLS_HOW_TO_BUY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('Explore subscriptions')}
                    </Button>
                  </CardFooter>
                </Card>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
};

export default withErrorBoundary(
  observer(MyActivity),
  ComponentError,
  handleError,
);
